import React, { createContext, useEffect, useState } from 'react';
import { useActives } from '../../../../hooks/useActives';
import axios from 'axios';
import { baseURL } from '../../../../utils/baseURL';
import { walletRoutes } from '../../../../utils/RoutesApi';

interface IPropsStrategyContextProvider {
  children: React.ReactNode;
}

interface IPropsStrategyContext {
  action: number;
  fiis: number;
  rendaFixa: number;
  stocks: number;
  etfs: number;
  reits: number;
  crypto: number;
  total: number;
  checkIfItIsBalanced: boolean;
  handleChangeActionPercentage: (percent: number) => void;
  handleChangeFiiPercentage: (percent: number) => void;
  handleChangeRendaFixaPercentage: (percent: number) => void;
  handleChangeStocksPercentage: (percent: number) => void;
  handleChangeEtfsPercentage: (percent: number) => void;
  handleChangeReitsPercentage: (percent: number) => void;
  handleChangeCryptoPercentage: (percent: number) => void;
  handleGetConfigStrategyWallet: () => void;
}

export const StrategyContext = createContext<IPropsStrategyContext>({
  action: 0,
  fiis: 0,
  rendaFixa: 0,
  stocks: 0,
  etfs: 0,
  reits: 0,
  crypto: 0,
  total: 0,
  checkIfItIsBalanced: false,
  handleChangeActionPercentage: () => {},
  handleChangeFiiPercentage: () => {},
  handleChangeRendaFixaPercentage: () => {},
  handleChangeStocksPercentage: () => {},
  handleChangeEtfsPercentage: () => {},
  handleChangeReitsPercentage: () => {},
  handleChangeCryptoPercentage: () => {},
  handleGetConfigStrategyWallet: () => {},
});

export const StrategyContextProvider = ({
  children,
}: IPropsStrategyContextProvider) => {
  // @ts-ignore
  const { percentage } = useActives();

  const [action, setAction] = useState(0);
  const [fiis, setFiis] = useState(0);
  const [rendaFixa, setRendaFixa] = useState(0);
  const [stocks, setStocks] = useState(0);
  const [etfs, setEtfs] = useState(0);
  const [reits, setReits] = useState(0);
  const [crypto, setCrypto] = useState(0);
  const [total, setTotal] = useState(0);
  const [checkIfItIsBalanced, setCheckIfItIsBalanced] = useState(false);

  useEffect(() => {
    handleGetConfigStrategyWallet();
  }, []);

  useEffect(() => {
    setCheckIfItIsBalanced(
      (percentage?.action || 0) >= action &&
        (percentage?.fii || 0) >= fiis &&
        (percentage?.rendaFixa || 0) >= rendaFixa &&
        (percentage?.stocks || 0) >= stocks &&
        (percentage?.etfs || 0) >= etfs &&
        (percentage?.reits || 0) >= reits &&
        (percentage?.crypto || 0) >= crypto
    );
  }, [action, fiis, rendaFixa, stocks, etfs, reits, crypto, percentage]);

  useEffect(() => {
    setTotal(action + fiis + rendaFixa + stocks + etfs + reits + crypto);
  }, [action, fiis, rendaFixa, stocks, etfs, reits, crypto]);

  function handleGetConfigStrategyWallet() {
    axios
      .get(`${baseURL}/api${walletRoutes.WALLET_GET_CONFIG_STRATEGY}`)
      .then((response) => {
        const data = response.data.data;

        setFiis(Number(data.PorcentagemFII || 0));
        setAction(Number(data.PorcentagemAcoes || 0));
        setRendaFixa(Number(data.PorcentagemRendaVariavel || 0));
        setStocks(Number(data.PorcentagemStock || 0));
        setEtfs(Number(data.PorcentagemEtf || 0));
        setReits(Number(data.PorcentagemReit || 0));
        setCrypto(Number(data.PorcentagemCripto || 0));
      })
      .catch((err) => {});
  }

  function getTotalWithoutAction() {
    return fiis + rendaFixa + stocks + etfs + reits + crypto;
  }

  function getTotalWithoutFii() {
    return action + rendaFixa + stocks + etfs + reits + crypto;
  }

  function getTotalWithoutRendaFixa() {
    return action + fiis + stocks + etfs + reits + crypto;
  }

  function getTotalWithoutStocks() {
    return action + fiis + rendaFixa + etfs + reits + crypto;
  }

  function getTotalWithoutEtfs() {
    return action + fiis + rendaFixa + stocks + reits + crypto;
  }

  function getTotalWithoutReits() {
    return action + fiis + rendaFixa + stocks + etfs + crypto;
  }

  function getTotalWithoutCrypto() {
    return action + fiis + rendaFixa + stocks + etfs + reits;
  }

  function handleChangeActionPercentage(percentagem: number) {
    const valor = Number(percentagem || 0);
    const totalWithout = getTotalWithoutAction();

    if (totalWithout + valor > 100) {
      setAction(Math.max(0, 100 - totalWithout));
    } else {
      setAction(valor);
    }
  }

  function handleChangeFiiPercentage(percentagem: number) {
    const valor = Number(percentagem || 0);
    const totalWithout = getTotalWithoutFii();

    if (totalWithout + valor > 100) {
      setFiis(Math.max(0, 100 - totalWithout));
    } else {
      setFiis(valor);
    }
  }

  function handleChangeRendaFixaPercentage(percentagem: number) {
    const valor = Number(percentagem || 0);
    const totalWithout = getTotalWithoutRendaFixa();

    if (totalWithout + valor > 100) {
      setRendaFixa(Math.max(0, 100 - totalWithout));
    } else {
      setRendaFixa(valor);
    }
  }

  function handleChangeStocksPercentage(percentagem: number) {
    const valor = Number(percentagem || 0);
    const totalWithout = getTotalWithoutStocks();

    if (totalWithout + valor > 100) {
      setStocks(Math.max(0, 100 - totalWithout));
    } else {
      setStocks(valor);
    }
  }

  function handleChangeEtfsPercentage(percentagem: number) {
    const valor = Number(percentagem || 0);
    const totalWithout = getTotalWithoutEtfs();

    if (totalWithout + valor > 100) {
      setEtfs(Math.max(0, 100 - totalWithout));
    } else {
      setEtfs(valor);
    }
  }

  function handleChangeReitsPercentage(percentagem: number) {
    const valor = Number(percentagem || 0);
    const totalWithout = getTotalWithoutReits();

    if (totalWithout + valor > 100) {
      setReits(Math.max(0, 100 - totalWithout));
    } else {
      setReits(valor);
    }
  }

  function handleChangeCryptoPercentage(percentagem: number) {
    const valor = Number(percentagem || 0);
    const totalWithout = getTotalWithoutCrypto();

    if (totalWithout + valor > 100) {
      setCrypto(Math.max(0, 100 - totalWithout));
    } else {
      setCrypto(valor);
    }
  }

  return (
    <StrategyContext.Provider
      value={{
        action,
        fiis,
        rendaFixa,
        stocks,
        etfs,
        reits,
        crypto,
        total,
        checkIfItIsBalanced,
        handleChangeActionPercentage,
        handleChangeFiiPercentage,
        handleChangeRendaFixaPercentage,
        handleChangeStocksPercentage,
        handleChangeEtfsPercentage,
        handleChangeReitsPercentage,
        handleChangeCryptoPercentage,
        handleGetConfigStrategyWallet,
      }}
    >
      {children}
    </StrategyContext.Provider>
  );
};